'use client';

import { createContext, useContext, useEffect, useRef } from 'react';
import {
	VaultAccount,
	Vault,
	VAULT_PROGRAM_ID,
	getDriftVaultProgram,
	VaultDepositor,
	VaultDepositorAccount,
} from '@drift-labs/vaults-sdk';
import { COMMON_UI_UTILS } from '@drift/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { PublicKey } from '@drift-labs/sdk';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import { useQueryClient } from '@tanstack/react-query';
import { vaultQueryKeys } from 'src/hooks/query/vaults/vaultQueryKeys';
import { VaultsOnChainDataLookup } from 'src/hooks/vaults/vaultStatsSyncUtils';
import { VaultDepositorAccountsLookup } from 'src/hooks/query/vaults/types';
import { isValidPublicKey } from 'src/utils/validation';

type VaultAccountContextType = {
	vaultPubkey: string;
};

const VaultAccountContext = createContext<VaultAccountContextType | null>(null);

/**
 * Subscribes to vault and depositor account updates for a specific vault.
 * Updates React Query cache when subscription events fire.
 */
export const SubscribedVaultProvider = (props: {
	children: React.ReactNode;
	vaultPubkey: string;
}) => {
	const { children, vaultPubkey } = props;
	const queryClient = useQueryClient();
	const authority = useCurrentAuthority();
	const connection = useDriftStore((s) => s.connection.current);
	const bulkAccountLoader = useDriftStore((s) => s.connection.accountLoader);

	const vaultAccountRef = useRef<VaultAccount | undefined>(undefined);
	const vaultUpdateHandlerRef = useRef<((data: Vault) => void) | undefined>(
		undefined
	);
	const vaultDepositorAccountRef = useRef<VaultDepositorAccount | undefined>(
		undefined
	);
	const vaultDepositorUpdateHandlerRef = useRef<
		((data: VaultDepositor) => void) | undefined
	>(undefined);

	// Subscribe to vault account updates
	useEffect(() => {
		// Skip subscription for external vaults with non-base58 identifiers
		if (!vaultPubkey || !connection || !bulkAccountLoader) return;
		if (!isValidPublicKey(vaultPubkey)) return;

		async function subscribeToVault() {
			if (!connection || !bulkAccountLoader || !vaultPubkey) return;

			const newWallet = COMMON_UI_UTILS.createPlaceholderIWallet();
			const driftVaultsProgram = getDriftVaultProgram(connection, newWallet);

			const vaultAccount = new VaultAccount(
				driftVaultsProgram,
				new PublicKey(vaultPubkey),
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				bulkAccountLoader as any // Type assertion needed due to duplicate @drift-labs/sdk instances
			);

			vaultAccountRef.current = vaultAccount;
			await vaultAccount.subscribe();

			const vaultAccountData = vaultAccount.getData();

			// Update React Query cache with initial data
			queryClient.setQueryData<VaultsOnChainDataLookup>(
				vaultQueryKeys.vaultOnChainData(),
				(prev) => {
					if (!prev) return prev;
					return {
						...prev,
						[vaultPubkey]: {
							...prev[vaultPubkey],
							vaultAccountData,
						},
					};
				}
			);

			const vaultUpdateHandler = (newVaultAccountData: Vault) => {
				// Merge into React Query cache
				queryClient.setQueryData<VaultsOnChainDataLookup>(
					vaultQueryKeys.vaultOnChainData(),
					(prev) => {
						if (!prev) return prev;
						return {
							...prev,
							[vaultPubkey]: {
								...prev[vaultPubkey],
								vaultAccountData: newVaultAccountData,
							},
						};
					}
				);
			};

			vaultUpdateHandlerRef.current = vaultUpdateHandler;
			vaultAccount.eventEmitter.on('vaultUpdate', vaultUpdateHandler);
		}

		subscribeToVault();

		return () => {
			if (vaultAccountRef.current && vaultUpdateHandlerRef.current) {
				vaultAccountRef.current.eventEmitter.off(
					'vaultUpdate',
					vaultUpdateHandlerRef.current
				);
			}
			vaultUpdateHandlerRef.current = undefined;
			vaultAccountRef.current?.unsubscribe();
			vaultAccountRef.current = undefined;
		};
	}, [vaultPubkey, connection, bulkAccountLoader, queryClient]);

	// Subscribe to vault depositor account updates
	useEffect(() => {
		// Skip subscription for external vaults with non-base58 identifiers
		if (!vaultPubkey || !authority || !bulkAccountLoader || !connection) {
			return;
		}
		if (!isValidPublicKey(vaultPubkey)) return;

		async function subscribeToVaultDepositor() {
			if (!connection || !bulkAccountLoader || !authority || !vaultPubkey)
				return;

			const vaultDepositorPubkey = VaultDepositorAccount.getAddressSync(
				VAULT_PROGRAM_ID,
				new PublicKey(vaultPubkey),
				authority
			);
			const newWallet =
				COMMON_UI_UTILS.createPlaceholderIWallet(vaultDepositorPubkey);
			const driftVaultsProgram = getDriftVaultProgram(connection, newWallet);

			const vaultDepositorAccount = new VaultDepositorAccount(
				driftVaultsProgram,
				vaultDepositorPubkey,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				bulkAccountLoader as any // Type assertion needed due to duplicate @drift-labs/sdk instances
			);
			vaultDepositorAccountRef.current = vaultDepositorAccount;

			await vaultDepositorAccount.subscribe();

			let vaultDepositorAccountData: VaultDepositor | undefined;
			try {
				vaultDepositorAccountData = vaultDepositorAccount.getData();
			} catch {
				// error means vault depositor is not initialized
			}

			// Update React Query cache with initial data if depositor exists
			if (vaultDepositorAccountData) {
				const vaultAccountData = vaultAccountRef.current?.getData();
				if (vaultAccountData) {
					queryClient.setQueryData<VaultDepositorAccountsLookup>(
						vaultQueryKeys.vaultDepositorAccounts(authority.toString()),
						(prev) => {
							return {
								...prev,
								[vaultPubkey]: {
									vaultDepositor: vaultDepositorAccountData,
									vault: vaultAccountData,
								},
							};
						}
					);
				}
			}

			const vaultDepositorUpdateHandler = (
				newVaultDepositorData: VaultDepositor
			) => {
				// Merge into React Query cache
				const vaultAccountData = vaultAccountRef.current?.getData();
				if (vaultAccountData) {
					queryClient.setQueryData<VaultDepositorAccountsLookup>(
						vaultQueryKeys.vaultDepositorAccounts(authority.toString()),
						(prev) => {
							return {
								...prev,
								[vaultPubkey]: {
									vaultDepositor: newVaultDepositorData,
									vault: vaultAccountData,
								},
							};
						}
					);
				}
			};

			vaultDepositorUpdateHandlerRef.current = vaultDepositorUpdateHandler;
			vaultDepositorAccount.eventEmitter.on(
				'vaultDepositorUpdate',
				vaultDepositorUpdateHandler
			);
		}

		subscribeToVaultDepositor();

		return () => {
			if (
				vaultDepositorAccountRef.current &&
				vaultDepositorUpdateHandlerRef.current
			) {
				vaultDepositorAccountRef.current.eventEmitter.off(
					'vaultDepositorUpdate',
					vaultDepositorUpdateHandlerRef.current
				);
			}
			vaultDepositorUpdateHandlerRef.current = undefined;
			vaultDepositorAccountRef.current?.unsubscribe();
			vaultDepositorAccountRef.current = undefined;
		};
	}, [vaultPubkey, authority, bulkAccountLoader, connection, queryClient]);

	return (
		<VaultAccountContext
			value={{
				vaultPubkey,
			}}
		>
			{children}
		</VaultAccountContext>
	);
};

export const useSubscribedVaultContext = () => {
	const context = useContext(VaultAccountContext);

	if (!context) {
		throw new Error(
			'useSubscribedVaultContext must be used within a SubscribedVaultProvider'
		);
	}

	return context;
};
